import { Controller } from "@hotwired/stimulus";

/**
 * Permission editor with chip-based toggles, group select-all,
 * row select-all, and counters.
 *
 * Targets:
 * - chip: hidden checkbox inputs for each permission
 * - groupCounter: span showing "N / M" per group
 * - groupToggle: select-all checkbox per group
 * - rowToggle: select-all checkbox per resource row
 * - totalCounter: span showing total enabled count
 */
export class PermissionEditorController extends Controller {
  static targets = [
    "chip",
    "groupCounter",
    "groupToggle",
    "rowToggle",
    "totalCounter",
    "saveButton",
  ];

  static confirmMessage =
    "You have unsaved changes, are you sure you want to leave this page?";

  connect() {
    this.updateAllCounters();
    this.snapshotInitialState();
    this.dirty = false;

    // Navigation guards
    this.beforeVisitListener = (event) => {
      if (this.dirty && !confirm(PermissionEditorController.confirmMessage)) {
        event.preventDefault();
      }
    };
    this.beforeUnloadListener = (event) => {
      if (this.dirty) {
        event.preventDefault();
        event.returnValue = "";
        return "";
      }
      delete event["returnValue"];
    };
    document.addEventListener("turbo:before-visit", this.beforeVisitListener);
    window.addEventListener("beforeunload", this.beforeUnloadListener);

    // Reset dirty on form submit
    const form = this.element.closest("form");
    if (form) {
      this.submitListener = () => (this.dirty = false);
      form.addEventListener("submit", this.submitListener);
    }
  }

  disconnect() {
    document.removeEventListener(
      "turbo:before-visit",
      this.beforeVisitListener
    );
    window.removeEventListener("beforeunload", this.beforeUnloadListener);

    const form = this.element.closest("form");
    if (form && this.submitListener) {
      form.removeEventListener("submit", this.submitListener);
    }
  }

  // Toggle a single chip's visual state when its label is clicked
  toggleChip(event) {
    const chip = event.currentTarget;
    const checkbox = chip.querySelector('input[type="checkbox"]');
    if (!checkbox || checkbox.disabled) return;

    this.updateChipVisual(chip, checkbox);

    const group = checkbox.dataset.group;
    const resource = checkbox.dataset.resource;
    this.updateGroupCounter(group);
    this.updateGroupToggleState(group);
    this.updateRowToggleState(resource, group);
    this.updateTotalCounter();
    this.updateDirtyState();
  }

  // Toggle all permissions in a group
  toggleGroup(event) {
    const checkbox = event.currentTarget;
    const group = checkbox.dataset.group;
    const isChecked = checkbox.checked;

    this.chipsForGroup(group).forEach((chip) => {
      if (!chip.disabled) {
        chip.checked = isChecked;
        this.updateChipVisual(chip.closest(".permission-chip"), chip);
      }
    });

    this.updateGroupCounter(group);
    // Update all row toggles within this group
    const resources = new Set(
      this.chipsForGroup(group).map((cb) => cb.dataset.resource)
    );
    resources.forEach((resource) => {
      this.updateRowToggleState(resource, group);
    });
    this.updateTotalCounter();
    this.updateDirtyState();
  }

  // Toggle all permissions in a resource row
  toggleRow(event) {
    const checkbox = event.currentTarget;
    const resource = checkbox.dataset.resource;
    const group = checkbox.dataset.group;
    const isChecked = checkbox.checked;

    this.chipsForRow(resource, group).forEach((chip) => {
      if (!chip.disabled) {
        chip.checked = isChecked;
        this.updateChipVisual(chip.closest(".permission-chip"), chip);
      }
    });

    this.updateGroupCounter(group);
    this.updateGroupToggleState(group);
    this.updateTotalCounter();
    this.updateDirtyState();
  }

  // --- Private helpers ---

  updateChipVisual(chipEl, checkbox) {
    if (!chipEl) return;
    chipEl.classList.toggle("selected", checkbox.checked);
  }

  chipsForGroup(group) {
    return this.chipTargets.filter((cb) => cb.dataset.group === group);
  }

  chipsForRow(resource, group) {
    return this.chipTargets.filter(
      (cb) => cb.dataset.resource === resource && cb.dataset.group === group
    );
  }

  updateGroupCounter(group) {
    const chips = this.chipsForGroup(group);
    const enabled = chips.filter((cb) => cb.checked).length;
    const total = chips.length;

    const counter = this.groupCounterTargets.find(
      (el) => el.dataset.group === group
    );
    if (counter) {
      counter.textContent = `${enabled} / ${total}`;

      // Toggle muted appearance for groups with no enabled permissions
      const details = counter.closest("details");
      if (details) {
        details.classList.toggle("permission-group-empty", enabled === 0);
      }
    }
  }

  updateGroupToggleState(group) {
    const toggle = this.groupToggleTargets.find(
      (el) => el.dataset.group === group
    );
    if (!toggle) return;

    const chips = this.chipsForGroup(group).filter((cb) => !cb.disabled);
    const checkedCount = chips.filter((cb) => cb.checked).length;

    if (checkedCount === 0) {
      toggle.checked = false;
      toggle.indeterminate = false;
    } else if (checkedCount === chips.length) {
      toggle.checked = true;
      toggle.indeterminate = false;
    } else {
      toggle.checked = false;
      toggle.indeterminate = true;
    }
  }

  updateRowToggleState(resource, group) {
    if (!resource) return;

    const toggle = this.rowToggleTargets.find(
      (el) => el.dataset.resource === resource && el.dataset.group === group
    );
    if (!toggle) return;

    const chips = this.chipsForRow(resource, group).filter(
      (cb) => !cb.disabled
    );
    const checkedCount = chips.filter((cb) => cb.checked).length;

    if (checkedCount === 0) {
      toggle.checked = false;
      toggle.indeterminate = false;
    } else if (checkedCount === chips.length) {
      toggle.checked = true;
      toggle.indeterminate = false;
    } else {
      toggle.checked = false;
      toggle.indeterminate = true;
    }
  }

  updateTotalCounter() {
    if (!this.hasTotalCounterTarget) return;
    const enabled = this.chipTargets.filter((cb) => cb.checked).length;
    const total = this.chipTargets.length;
    this.totalCounterTarget.textContent = `${enabled} / ${total}`;
  }

  updateAllCounters() {
    const groups = new Set(this.chipTargets.map((cb) => cb.dataset.group));
    groups.forEach((group) => {
      this.updateGroupCounter(group);
      this.updateGroupToggleState(group);

      // Update row toggles within this group
      const resources = new Set(
        this.chipsForGroup(group).map((cb) => cb.dataset.resource)
      );
      resources.forEach((resource) => {
        this.updateRowToggleState(resource, group);
      });
    });

    this.updateTotalCounter();
  }

  // --- Dirty state tracking ---

  snapshotInitialState() {
    this.initialState = new Map();
    this.chipTargets.forEach((cb) => {
      this.initialState.set(cb.value, cb.checked);
    });
  }

  updateDirtyState() {
    this.dirty = this.chipTargets.some(
      (cb) => cb.checked !== this.initialState.get(cb.value)
    );

    this.saveButtonTargets.forEach((btn) => {
      btn.disabled = !this.dirty;
      btn.closest("[data-save-wrapper]")?.classList.toggle("d-none", !this.dirty);
    });

    const summaryBar = this.element.querySelector(".permission-summary-bar");
    if (summaryBar) {
      summaryBar.classList.toggle("dirty", this.dirty);
    }
  }
}
